import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PaymentForm = ({ 
  onSubmit = () => {}, 
  isProcessing = false,
  savedPaymentMethods = [],
  isAuthenticated = false,
  errors = {}
}) => {
  const [paymentData, setPaymentData] = useState({
    useExistingCard: false,
    selectedCardId: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    billingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    saveCard: false,
    emailUpdates: true,
    smsUpdates: false
  });

  const stateOptions = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'NY', label: 'New York' },
    { value: 'TX', label: 'Texas' }
  ];

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'MX', label: 'Mexico' }
  ];

  const savedCardOptions = savedPaymentMethods?.map(card => ({
    value: card?.id,
    label: `**** **** **** ${card?.last4} (${card?.brand?.toUpperCase()})`
  }));

  const handleInputChange = (field, value) => {
    if (field?.includes('.')) {
      const [parent, child] = field?.split('.');
      setPaymentData(prev => ({
        ...prev,
        [parent]: {
          ...prev?.[parent],
          [child]: value
        }
      }));
    } else {
      setPaymentData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(paymentData);
  };

  const formatCardNumber = (value) => {
    const v = value?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    const matches = v?.match(/\d{4,16}/g);
    const match = matches && matches?.[0] || '';
    const parts = [];
    for (let i = 0, len = match?.length; i < len; i += 4) {
      parts?.push(match?.substring(i, i + 4));
    }
    if (parts?.length) {
      return parts?.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    const v = value?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    if (v?.length >= 2) {
      return v?.substring(0, 2) + '/' + v?.substring(2, 4);
    }
    return v;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="CreditCard" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Payment Information</h2>
        <div className="flex items-center space-x-1 ml-auto">
          <Icon name="Shield" size={16} className="text-success" />
          <span className="text-xs text-success font-medium">Secure</span>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Saved Payment Methods */}
        {isAuthenticated && savedPaymentMethods?.length > 0 && (
          <div className="space-y-4">
            <Checkbox
              label="Use saved payment method"
              checked={paymentData?.useExistingCard}
              onChange={(e) => handleInputChange('useExistingCard', e?.target?.checked)}
            />
            
            {paymentData?.useExistingCard && (
              <Select
                label="Select Payment Method"
                options={savedCardOptions}
                value={paymentData?.selectedCardId}
                onChange={(value) => handleInputChange('selectedCardId', value)}
                placeholder="Choose a saved card"
                error={errors?.selectedCardId}
              />
            )}
          </div>
        )}

        {/* New Card Information */}
        {!paymentData?.useExistingCard && (
          <div className="space-y-4">
            <Input
              label="Cardholder Name"
              type="text"
              placeholder="Full name on card"
              value={paymentData?.cardholderName}
              onChange={(e) => handleInputChange('cardholderName', e?.target?.value)}
              error={errors?.cardholderName}
              required
            />

            <Input
              label="Card Number"
              type="text"
              placeholder="1234 5678 9012 3456"
              value={paymentData?.cardNumber}
              onChange={(e) => handleInputChange('cardNumber', formatCardNumber(e?.target?.value))}
              error={errors?.cardNumber}
              maxLength={19}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Expiry Date"
                type="text"
                placeholder="MM/YY"
                value={paymentData?.expiryDate}
                onChange={(e) => handleInputChange('expiryDate', formatExpiryDate(e?.target?.value))}
                error={errors?.expiryDate}
                maxLength={5}
                required
              />

              <Input
                label="CVV"
                type="text"
                placeholder="123"
                value={paymentData?.cvv}
                onChange={(e) => handleInputChange('cvv', e?.target?.value?.replace(/\D/g, ''))}
                error={errors?.cvv}
                maxLength={4}
                required
              />
            </div>
          </div>
        )}

        {/* Billing Address */}
        <div className="space-y-4">
          <h3 className="font-medium text-foreground">Billing Address</h3>
          
          <Input
            label="Street Address"
            type="text"
            placeholder="123 Main Street"
            value={paymentData?.billingAddress?.street}
            onChange={(e) => handleInputChange('billingAddress.street', e?.target?.value)}
            error={errors?.['billingAddress.street']}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="City"
              type="text"
              placeholder="New York"
              value={paymentData?.billingAddress?.city}
              onChange={(e) => handleInputChange('billingAddress.city', e?.target?.value)}
              error={errors?.['billingAddress.city']}
              required
            />

            <Select
              label="State"
              options={stateOptions}
              value={paymentData?.billingAddress?.state}
              onChange={(value) => handleInputChange('billingAddress.state', value)}
              placeholder="Select state"
              error={errors?.['billingAddress.state']}
              searchable
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="ZIP Code"
              type="text"
              placeholder="10001"
              value={paymentData?.billingAddress?.zipCode}
              onChange={(e) => handleInputChange('billingAddress.zipCode', e?.target?.value)}
              error={errors?.['billingAddress.zipCode']}
              required
            />

            <Select
              label="Country"
              options={countryOptions}
              value={paymentData?.billingAddress?.country}
              onChange={(value) => handleInputChange('billingAddress.country', value)}
              error={errors?.['billingAddress.country']}
              required
            />
          </div>
        </div>

        {/* Preferences */}
        <div className="space-y-4 pt-4 border-t border-border">
          <h3 className="font-medium text-foreground">Preferences</h3>
          
          {isAuthenticated && !paymentData?.useExistingCard && (
            <Checkbox
              label="Save this card for future purchases"
              description="Securely store payment method for faster checkout"
              checked={paymentData?.saveCard}
              onChange={(e) => handleInputChange('saveCard', e?.target?.checked)}
            />
          )}

          <Checkbox
            label="Email notifications"
            description="Receive order confirmations and event updates"
            checked={paymentData?.emailUpdates}
            onChange={(e) => handleInputChange('emailUpdates', e?.target?.checked)}
          />

          <Checkbox
            label="SMS notifications"
            description="Get text messages for important updates"
            checked={paymentData?.smsUpdates}
            onChange={(e) => handleInputChange('smsUpdates', e?.target?.checked)}
          />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isProcessing}
          iconName="Lock"
          iconPosition="left"
          className="w-full"
          disabled={isProcessing}
        >
          {isProcessing ? 'Processing Payment...' : 'Complete Purchase'}
        </Button>

        {/* Security Notice */}
        <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Shield" size={14} />
          <span>Your payment information is encrypted and secure</span>
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;